import { getCogoPlatformBase, readStudioEnv } from "@/services/cogo-platform-config"
import { isApplianceDistribution } from "@/services/studio-presentation-profile"

/**
 * Where `/api/chat-gateway` is, or the fact that this build does not have one.
 *
 * ── WHY THIS IS ONE FUNCTION AND NOT THREE EXPRESSIONS ────────────────────
 *
 * Three production sites each spelled `VITE_CHAT_GATEWAY_URL ||
 * getCogoPlatformBase()` for themselves — `chat-model-adapter.ts` (the chat
 * panel), `preview-proposal-chat-service.ts`, and
 * `document-template-model-service.ts`. All three hand the result to
 * `GatewayChatService`, which appends `/api/chat-gateway`, so all three carried
 * the same wrong answer on the appliance and would have to be corrected three
 * times. One resolver, asked by all three.
 *
 * ── WHY THE FALLBACK WAS WRONG ────────────────────────────────────────────
 *
 * `getCogoPlatformBase()` is the base of the platform REST API. On CLOUD that
 * happens to be the same origin as the chat gateway — `platform.cogo.xyz` is
 * the Cloudflare worker (`cogo-platform/apps/cogo-cloudflare`), which is the
 * only place in the product where `/api/chat-gateway` is implemented. On the
 * APPLIANCE the two diverge: `.env.appliance` points the platform base at
 * `/api/platform`, which the edge proxies to `cogo-platform-api` — a different
 * app, with zero `chat` references in its source and no such route. Measured
 * live 2026-08-26: that request returns 404 `{"error":"not_found"}`, which the
 * chat panel then narrated to a creator as `Gateway error (404): not_found`.
 *
 * So one env var was standing in for two different services, and the implicit
 * fallback is only ever valid where those two services are the same host.
 *
 * ── THE TWO WAYS A BUILD SAYS "NO GATEWAY" ────────────────────────────────
 *
 *  1. DECLARATION — `VITE_CHAT_GATEWAY_URL` present and empty. Present-and-
 *     empty is a statement; missing entirely would leave a reader unable to
 *     tell the decision from an oversight. This is the same convention
 *     `.env.production` uses for `VITE_OPENPENCIL_IMPORT_API_URL`, and it is
 *     what `.env.appliance` said until 2026-08-26. It says `/api/platform`
 *     now — see the closing paragraph — so the empty declaration is currently
 *     exercised only by the tests, not by a shipped build.
 *  2. FLOOR — appliance distribution with no explicit URL at all. A build that
 *     forgets the declaration must not silently go back to dialling the
 *     platform REST API; it must still refuse. Asserted separately from (1) in
 *     `__tests__/chat-gateway-unconfigured.test.ts` so neither can rot behind
 *     the other.
 *
 * An explicit non-empty URL wins on every distribution, so an appliance that
 * one day DOES route a gateway just says so and is believed — the floor is not
 * a ceiling.
 */
export interface ChatGatewayEnv {
  VITE_CHAT_GATEWAY_URL?: string
  VITE_STUDIO_DISTRIBUTION?: string
}

/**
 * The error code `GatewayChatService` yields when it refuses to send.
 *
 * Distinct from `GATEWAY_ERROR`, which means a gateway answered and failed.
 * Collapsing the two is the defect: it reports a transport that was never
 * configured as one that broke.
 */
export const CHAT_GATEWAY_UNCONFIGURED_CODE = "GATEWAY_NOT_CONFIGURED"

/**
 * What the creator reads when this build has no chat gateway.
 *
 * The deployment-scoped shape is the product's existing one for a backend that
 * is not there — `mutationBlockedNoGateway` in `i18n/studio-dictionaries.ts`:
 * "이 배포에는 온톨로지 게이트웨이가 없어 블록을 바꿀 수 없습니다." The second
 * sentence is the move the reader can make, which every refusal in
 * `assign-chat-to-agent-runtime.ts` (#2615) and `agent-reply-copy.ts` (#2616)
 * ends with. Nothing here is addressed to an integrator: a creator cannot act
 * on "404", and this is not their request's fault.
 *
 * Kept local rather than folded into `agent-reply-copy.ts` for the reason
 * `assign-chat-to-agent-runtime.ts:47-55` gives about its own three sentences:
 * `AgentLocalFact` is the stated long-term home for "facts a screen produces
 * for itself", and these should move there together when that lane lands.
 */
export const CHAT_GATEWAY_UNCONFIGURED_SENTENCE =
  "이 배포에는 채팅 게이트웨이가 없어 메시지를 보낼 수 없습니다. 관리자에게 채팅 게이트웨이 연결을 요청해 주세요."

/**
 * The base to POST `/api/chat-gateway` to, or `null` when this build has none.
 *
 * `null` is not an error and not an empty string — it is an answer, and the
 * caller must say it rather than send anything.
 */
export function resolveChatGatewayBase(env?: ChatGatewayEnv): string | null {
  const declared = env?.VITE_CHAT_GATEWAY_URL ?? readStudioEnv("VITE_CHAT_GATEWAY_URL")

  if (declared !== undefined) {
    const explicit = declared.trim().replace(/\/+$/, "")
    // Declared and empty: this deployment states it has no chat gateway.
    return explicit.length > 0 ? explicit : null
  }

  // Undeclared. The implicit platform-base fallback is a CLOUD fallback; on the
  // appliance it resolves to an app that does not serve this route.
  if (isApplianceDistribution(env)) return null

  return getCogoPlatformBase()
}
